import CapstoneImage from "../assets/capstone-proj-screenshot.jpg";
import FrequencyResponseImage from "../assets/frequency-response.png";
import PhasorEstimationImage from "../assets/phasor-estimation.png";
import HevBevMatlabImage from "../assets/hev_bev_matlab.png";
import HevBevResultsImage from "../assets/hev_bev_results_plots.png";
import ConstructionScheduleImage from "../assets/project_management_plan_phase_1.png";
import ConstructionRiskPlanImage from "../assets/project_management_risk_mgmt_plan.png";
import SensorPlannerImage from "../assets/sensor_planner.png";
import SensorPlannerOptimizationImage from "../assets/sensor_planner_pso_ga_layouts.png";

export interface ProjectEvidenceImage {
  image: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
}

export interface EngineeringProject {
  id: string;
  navLabel: string;
  title: string;
  context: string;
  role: string;
  objective: string;
  highlights: string[];
  outcome: string;
  methods: string[];
  evidence?: {
    images: ProjectEvidenceImage[];
    layout: "single" | "stacked" | "desktop-pair" | "wide-pair";
  };
  links?: {
    label: string;
    url: string;
  }[];
}

export const engineeringProjects: EngineeringProject[] = [
  {
    id: "arc-fault",
    navLabel: "Arc Fault Detection",
    title: "Arc Fault Detection System",
    context: "Undergraduate capstone",
    role: "Team Lead | 3-member engineering team",
    objective:
      "Develop an Arduino-based prototype that detects predefined electrical arc-fault conditions and provides remote fault notification.",
    highlights: [
      "Led a 3-member engineering team in developing the sensing and notification prototype.",
      "Integrated sensing, embedded control, Wi-Fi communication, and IFTTT-based SMS alerts for remote notification.",
      "Developed an SQL-based logging system to record fault events and retrieve historical notification data.",
      "Coordinated hardware/software integration, testing, task allocation, and final project deliverables.",
    ],
    outcome:
      "Integrated academic prototype supporting detection, notification, testing, and event logging.",
    methods: [
      "Arduino",
      "Embedded systems",
      "Wi-Fi",
      "IFTTT",
      "SQL",
      "System integration",
      "Testing",
    ],
    evidence: {
      layout: "single",
      images: [
        {
          image: CapstoneImage,
          alt: "Arc fault detection prototype with Arduino, breadboard, outlet module, and mobile warning interfaces",
          caption:
            "Capstone prototype hardware and remote notification interfaces.",
          width: 1280,
          height: 720,
        },
      ],
    },
    links: [
      {
        label: "View capstone demo video",
        url: "https://drive.google.com/file/d/1kzKO8vva8IF2pJV-VUExGpKP8mYJUerR/view?usp=sharing",
      },
    ],
  },
  {
    id: "power-system-algorithms",
    navLabel: "Power System Algorithms",
    title: "Power System Protection & Signal Estimation Algorithms",
    context: "MEng Smart Grid Coursework",
    role: "MATLAB algorithm implementation and evaluation",
    objective:
      "Implemented and evaluated MATLAB algorithms for power-system signal measurement and numerical-relay applications, including phasor magnitude and phase estimation, frequency estimation, and short-window signal-processing methods.",
    highlights: [
      "Implemented trigonometric, DFT-based, half-cycle, cosine, least-error-square, and frequency-estimation algorithms in MATLAB.",
      "Analyzed sampled electrical waveforms using magnitude, phase, frequency-response, and complex-phasor representations.",
      "Applied concepts including phasors, sampling, harmonics, CT/PT measurement, anti-aliasing, frequency estimation, and protection speed/selectivity to power-system monitoring and fault-analysis exercises.",
    ],
    outcome:
      "MATLAB implementations and analysis outputs for sampled power-system signals and numerical-relay coursework.",
    methods: [
      "MATLAB",
      "Phasor estimation",
      "DFT",
      "Frequency estimation",
      "Sampled signal analysis",
      "Numerical-relay concepts",
    ],
    evidence: {
      layout: "desktop-pair",
      images: [
        {
          image: FrequencyResponseImage,
          alt: "MATLAB magnitude and phase frequency-response plots for a power-system signal estimation algorithm",
          caption:
            "Algorithm frequency response showing magnitude and phase characteristics across the sampled frequency range.",
          width: 738,
          height: 610,
        },
        {
          image: PhasorEstimationImage,
          alt: "MATLAB phasor magnitude and phase-angle estimates plotted over time",
          caption:
            "MATLAB phasor-estimation output showing magnitude response and phase-angle tracking for a sampled electrical waveform.",
          width: 753,
          height: 606,
        },
      ],
    },
    links: [
      {
        label: "View MATLAB Implementations on GitHub",
        url: "https://github.com/suhas-sunder/smart-grid-matlab-algorithms-project",
      },
    ],
  },
  {
    id: "bev-simulation",
    navLabel: "BEV Simulation",
    title: "Hybrid Electric Vehicle / Battery Electric Vehicle Simulation",
    context: "MEng course project",
    role: "MATLAB / Simulink modelling",
    objective:
      "Evaluate vehicle energy use and regenerative-braking performance through a closed-loop battery-electric-vehicle model.",
    highlights: [
      "Developed a closed-loop MATLAB/Simulink BEV model integrating the motor, inverter, battery, energy-management strategy, and longitudinal vehicle dynamics.",
      "Implemented regenerative and friction brake blending under thermal, state-of-charge, and inverter constraints.",
      "Evaluated performance across UDDS, WLTP, and HWFET drive cycles.",
      "Quantified up to approximately 2% SOC recovery in urban driving and identified practical limitations of unconstrained energy-management models.",
    ],
    outcome:
      "Up to approximately 2% SOC recovery in urban driving, with practical limitations identified in unconstrained energy-management assumptions.",
    methods: [
      "MATLAB",
      "Simulink",
      "BEV modelling",
      "Motor and inverter",
      "Battery",
      "Energy management",
      "Regenerative braking",
      "Drive-cycle analysis",
    ],
    evidence: {
      layout: "wide-pair",
      images: [
        {
          image: HevBevMatlabImage,
          alt: "MATLAB and Simulink brake-split controller and closed-loop HEV and BEV model block diagrams",
          caption: "Brake-split controller and closed-loop vehicle model.",
          width: 3315,
          height: 1119,
        },
        {
          image: HevBevResultsImage,
          alt: "WLTP simulation plots comparing actual and desired speed, regenerative and friction braking forces, and battery state of charge",
          caption:
            "WLTP drive-cycle response, braking-force allocation, and state-of-charge results.",
          width: 3304,
          height: 1852,
        },
      ],
    },
  },
  {
    id: "construction-planning",
    navLabel: "Construction Planning",
    title: "Engineering Construction Planning",
    context: "MEng course project",
    role: "Project Manager / Team Lead | 7-member engineering team",
    objective:
      "Develop a complete project-management plan for a regulated construction project under fixed deadlines.",
    highlights: [
      "Led a 7-member engineering team across scope, schedule, cost, quality, risk, procurement, stakeholder, and change management planning.",
      "Built project controls including a WBS, Gantt schedule, critical path analysis, burn-down tracking, and risk-management plan.",
      "Covered permitting, procurement, construction, inspection, and closeout activities.",
      "Developed and maintained a quantitative RPN risk register across schedule, cost, safety, environmental, and regulatory risks.",
    ],
    outcome:
      "Delivered an integrated management plan, schedule controls, and quantitative risk register with assigned mitigation actions.",
    methods: [
      "WBS",
      "Gantt scheduling",
      "Critical path",
      "Risk register",
      "RPN",
      "Stakeholder management",
      "Ontario Building Code",
      "OHSA",
    ],
    evidence: {
      layout: "wide-pair",
      images: [
        {
          image: ConstructionScheduleImage,
          alt: "Phase 1 construction project schedule beside the reviewed deck design drawing",
          caption: "Phase 1 project schedule and reviewed design drawing.",
          width: 3088,
          height: 1251,
        },
        {
          image: ConstructionRiskPlanImage,
          alt: "Construction risk management plan with RPN categories, assumptions log, and RPN ranking table",
          caption:
            "Risk-management categories, assumptions log, and RPN reference table.",
          width: 3073,
          height: 1618,
        },
      ],
    },
  },
  {
    id: "sensor-planner",
    navLabel: "Sensor Planner",
    title: "Smart Home Sensor Planner",
    context: "IoT planning & simulation platform",
    role: "Systems modelling / optimization / full-stack delivery",
    objective:
      "Model smart-home sensor placement, connectivity, and automation workflows while evaluating placement tradeoffs.",
    highlights: [
      "Built and deployed a full-stack IoT planning and simulation platform for modelling sensor placement, connectivity, and automation workflows.",
      "Developed an interactive system using React, Python APIs, and a relational database for layout modelling, sensor configuration, event logging, and rule-based simulation.",
      "Developed a hybrid PSO-GA optimization algorithm in Python to evaluate sensor-placement tradeoffs.",
      "Evaluated near-optimal configurations against coverage, overlap, cost, and energy-related constraints.",
    ],
    outcome:
      "Deployed platform supports scenario modelling and the comparison of near-optimal sensor configurations.",
    methods: [
      "Python",
      "React",
      "Relational database",
      "IoT simulation",
      "PSO-GA optimization",
      "Systems modelling",
    ],
    evidence: {
      layout: "wide-pair",
      images: [
        {
          image: SensorPlannerImage,
          alt: "Smart Home Sensor Planner interface showing a multi-floor layout, configured sensors, and coverage areas",
          caption: "Deployed Sensor Planner layout-modelling interface.",
          width: 3811,
          height: 1951,
        },
        {
          image: SensorPlannerOptimizationImage,
          alt: "PSO-GA sensor placement results across multiple floor-plan layouts with coverage circles and overlap",
          caption: "PSO-GA placement results across varied floor-plan layouts.",
          width: 2802,
          height: 1576,
        },
      ],
    },
    links: [
      {
        label: "Visit Sensor Planner",
        url: "https://sensor-planner.netlify.app/",
      },
      {
        label: "View Sensor Planner source",
        url: "https://github.com/suhas-sunder/sensor-planner",
      },
    ],
  },
];

export const skillGroups = [
  {
    title: "ENGINEERING TOOLS",
    items: ["MATLAB", "Simulink", "AutoCAD Web", "Siemens NX"],
  },
  {
    title: "ENGINEERING ANALYSIS",
    items: [
      "Systems modelling",
      "Optimization",
      "Engineering calculations",
      "Testing",
      "Troubleshooting",
    ],
  },
  {
    title: "PROJECT DELIVERY",
    items: [
      "Requirements analysis",
      "WBS",
      "Gantt scheduling",
      "Critical path analysis",
      "Risk registers",
      "RPN",
      "Technical documentation",
      "Stakeholder coordination",
    ],
  },
  {
    title: "PROGRAMMING & DATA",
    items: [
      "Python, JavaScript, TypeScript",
      "React, Remix, HTML, CSS, Tailwind CSS, Sass",
      "Node.js, Express, Flask, RESTful APIs",
      "PostgreSQL, MySQL, Prisma ORM",
      "Jest, React Testing Library, Vitest",
      "Git, Postman, pgAdmin, Cloudflare CDN, Cloudflare R2",
    ],
  },
];

export const educationItems = [
  {
    degree: "Master of Engineering in Electrical and Computer Engineering",
    institution: "Ontario Tech University",
    location: "Oshawa, ON, Canada",
    date: "December 2025",
    distinction: "GPA: 4.21 / 4.30",
    coursework: [
      "Programming Methods & Abstraction",
      "Foundations of Software Engineering",
      "Engineering Communication and Ethics",
      "Project Management for Engineers",
      "Advanced Engineering Mathematics",
      "Smart Grid Technology",
      "Electric Vehicles",
      "Advanced Optimization",
      "Pervasive & Mobile Computing",
    ],
    leadership: [
      {
        title: "Orientation Leader",
        detail:
          "Supported campus-wide onboarding for 2,300+ incoming students during the Fall 2025 multi-day orientation.",
      },
      {
        title: "Engineering Planning Project Team Lead",
        detail:
          "Led a 7-member team using the Waterfall methodology to delegate work, run weekly progress meetings, resolve conflicts, and keep deliverables on schedule. Represented the team in meetings with the professor and presented the final plan.",
      },
    ],
  },
  {
    degree:
      "Bachelor of Engineering in Electrical Engineering and Management (Honours)",
    institution: "Ontario Tech University",
    location: "Oshawa, ON, Canada",
    date: "May 2019",
    distinction: "",
    coursework: [
      "Power Systems",
      "Electric Machines",
      "Fundamentals of Smart Grid",
      "Introduction to Power Electronics",
      "Introduction to Control Systems",
      "Advanced Control Systems",
      "Design Principles & Project Management",
      "Engineering Operations & Project Management",
    ],
    leadership: [
      {
        title: "Capstone Project Team Lead",
        detail:
          "Led a 3-person team developing an embedded fault-detection system, built a mobile app for receiving fault notifications, used SQL to manage notification records, and configured IFTTT to automate SMS alerts.",
      },
    ],
  },
];

export const experienceItems = [
  {
    id: "dobson-partners",
    company: "Dobson Partners",
    legalName: "8906386 Canada Limited",
    location: "Toronto, ON, Canada",
    roles: [
      {
        title: "Full-Stack Software Developer",
        dates: "Nov 2023 - Mar 2024 | Oct 2024 - Jun 2025",
      },
    ],
    highlights: [
      "Built and delivered full-stack platform features for landlords, tenants, and realtors, covering document management, messaging, identity verification, media uploads, and transaction workflows.",
      "Tested, troubleshot, and refined production features based on issues and feedback from early-access users, management, marketing, and real estate industry investors.",
      "Designed Prisma ORM data models and PostgreSQL schemas for users, documents, messages, and verification records, supporting consistent data storage and reliable CRUD operations.",
      "Developed React and RESTful API workflows for multi-step verification, document management, messaging, authentication, Cloudinary media uploads, and Stripe payment processing.",
      "Translated UI/UX wireframes and evolving stakeholder requirements into reusable React and Tailwind CSS components, while tracking milestone work in OpenProject and contributing to weekly cross-functional reviews.",
    ],
  },
  {
    id: "ats-group",
    company: "ATS Group Inc.",
    legalName: "",
    location: "Toronto, ON, Canada",
    roles: [
      {
        title: "Full-Stack Web Application Developer (Freelance)",
        dates: "Jan 2023 - Aug 2023",
      },
      {
        title: "Software Consulting Intern",
        dates: "Apr 2021 - Apr 2022",
      },
    ],
    highlights: [
      "Raised post-release client feedback scores from 3/5 to 5/5 by identifying major usability pain points with management and clients, then delivering targeted UI/UX improvements across facilities-maintenance workflows.",
      "Refactored features in a full-stack facilities-maintenance platform, improving employee records, maintenance history, charting, and data-visualization workflows.",
      "Designed, developed, and deployed a mobile-first client-facing website for All Track System, owning implementation end to end and deploying through AWS CloudFront and Amazon S3.",
      "Improved front-end quality by implementing automated tests with Jest and React Testing Library and resolving accessibility and Google Search Console issues.",
      "Built interactive prototypes and created floor-plan mockups in AutoCAD Web to test facilities-management workflows, communicate product concepts, and help define GPS tracking, Google Maps integration, proof-of-work submissions, repair status updates, and role-based access controls.",
      "Led demos, technical presentations, and project discussions to align technical and non-technical stakeholders.",
    ],
  },
  {
    id: "eme-group",
    company: "EME Group Inc.",
    legalName: "",
    location: "Toronto, ON, Canada",
    roles: [
      {
        title: "Web Developer (Freelance)",
        dates: "Nov 2022 - Dec 2022",
      },
    ],
    highlights: [
      "Built and launched the first client-facing website in more than 20 years for an electrical and mechanical engineering firm, contributing to a reported 200%+ increase in inbound inquiries.",
      "Developed a mobile-responsive website using HTML, CSS, JavaScript, Sass, and the Google Maps API, and continue to provide occasional domain configuration, hosting, and site-maintenance support.",
    ],
  },
];
